import { apiClient } from './apiClient';

/**
 * Internal helper for RPC notifications calls
 */
async function rpcNotifications<T>(company: string, action: string, payload: any): Promise<T> {
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/notifications/${action}`, payload, {
      withCredentials: true,
      // Accept all status codes to handle errors gracefully
      validateStatus: () => true,
    });
    // If backend returns { ok, data }, unwrap it here
    if ((data as any)?.ok && 'data' in data) {
      return (data as any).data as T;
    }
    // If backend returns { ok: true, notifications } or { ok: true, message }, return the data directly
    if ((data as any)?.ok) {
      // For responses like { ok: true, notifications } or { ok: true, message }
      const { ok, ...rest } = data as any;
      return rest as T;
    }
    return data as T;
  } catch (err: any) {
    // Handle connection errors gracefully - return empty/default data instead of throwing
    // This prevents UI breaking when notifications service is unavailable
    if (err.code === 'ECONNREFUSED' || err.code === 'ERR_CONNECTION_REFUSED' || 
        err.code === 'ETIMEDOUT' || err.code === 'ENOTFOUND' || 
        err.message?.includes('Connection refused') || err.message?.includes('timeout')) {
      // Return empty result based on action type
      if (action === 'list') {
        return { notifications: [] } as T;
      }
      if (action === 'unread-count') {
        return { count: 0 } as T;
      }
      return {} as T;
    }
    // Re-throw other errors
    throw err;
  }
}

/**
 * Get notifications for a company
 */
export async function getNotifications(company: string) {
  const result = await rpcNotifications<{ notifications: any[] }>(company, 'list', {});
  return result;
}

/**
 * Mark notification as read
 */
export async function markNotificationRead(company: string, notifId: string) {
  await rpcNotifications<{ message?: string }>(company, 'mark-read', { id: notifId });
}

/**
 * Delete notification
 */
export async function deleteNotification(company: string, notifId: string) {
  await rpcNotifications<{ message?: string }>(company, 'delete', { id: notifId });
}

/**
 * Send notification email
 * Now uses RPC: POST /api/:company/rpc/notifications/send
 */
export async function sendNotification(company: string, payload: { subject: string; message: string }) {
  const result = await rpcNotifications<{ message: string }>(company, 'send', payload);
  return result;
}

